import { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useApplicationConfig } from '@bonusly/application';
import { Box } from '@bonusly/design-system';

function toRailsPath(pathname: string): string {
	const shellPrefix = pathname.startsWith('/super_admin/') ? '/super_admin' : '/admin';
	const afterShell = pathname.slice(shellPrefix.length);

	if (afterShell.startsWith('/r/') || afterShell === '/r') {
		return afterShell.slice(2) || '/';
	}

	return pathname;
}

/**
 * Reads the current seasonal color mode from Kaleidoscope's localStorage so it
 * can be passed to the Rails iframe as a URL param. Must run in Kaleidoscope's
 * rendering context — the Rails iframe is a different origin and cannot access
 * this localStorage directly.
 *
 * Returns 'light' | 'dark' when a seasonal theme is active, null otherwise.
 */
function getSeasonalColorMode(): 'light' | 'dark' | null {
	try {
		// active-mode is written synchronously in toggleTheme (new code path).
		const activeMode = localStorage.getItem('bonusly:seasonal-theme:active-mode');
		const strategy = localStorage.getItem('bonusly:seasonal-theme:color-mode-strategy');

		// Fallback: scan for a preference key set by older toggleTheme code.
		// Preference keys match bonusly:seasonal-theme:<name> with no further colons.
		const EXCLUDED = new Set(['bonusly:seasonal-theme:active-mode', 'bonusly:seasonal-theme:color-mode-strategy', 'bonusly:seasonal-theme:saved-color-mode']);
		let isActive = activeMode === 'dark' || activeMode === 'light';
		if (!isActive) {
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && /^bonusly:seasonal-theme:[^:]+$/.test(k) && !EXCLUDED.has(k) && localStorage.getItem(k) === 'true') {
					isActive = true;
					break;
				}
			}
		}

		if (!isActive) return null;

		if (strategy === 'force-dark') return 'dark';
		// time-based (spring) or unknown strategy: derive from current hour
		const hour = new Date().getHours();
		return hour < 6 || hour >= 19 ? 'dark' : 'light';
	} catch {
		return null;
	}
}

/**
 * Renders a server-rendered Rails page inside an iframe, keeping the
 * Kaleidoscope shell (admin nav, Bizy side panel) mounted around it.
 *
 * Links inside the iframe use <base target="_top"> which navigates the top
 * window. Rails detects these top-level navigations (via Sec-Fetch-Dest)
 * and redirects back to the equivalent Kaleidoscope route, preserving the
 * admin shell. The iframe key includes search params so it remounts cleanly
 * when query params change (e.g., pagination).
 *
 * seasonal_color_mode is appended when a seasonal theme is active so the
 * Rails server can render the correct initial color mode. The iframe is a
 * different origin and cannot read Kaleidoscope's localStorage directly.
 */
export const AdminLegacyPage: FC = () => {
	const recognizeOrigin = useApplicationConfig('recognizeOrigin');
	const location = useLocation();
	// Computed once on mount — stable for the lifetime of this route visit.
	// Recomputed on full remount (navigation away and back, or full page reload).
	const [seasonalMode] = useState(() => getSeasonalColorMode());

	const railsPath = toRailsPath(location.pathname);
	const separator = location.search ? `${location.search}&_iframe=1` : '?_iframe=1';
	const seasonalParam = seasonalMode ? `&seasonal_color_mode=${seasonalMode}` : '';
	const iframeSrc = `${recognizeOrigin}${railsPath}${separator}${seasonalParam}`;

	return (
		<Box w="100%" h="100%">
			<iframe
				key={`${location.pathname}${location.search}`}
				src={iframeSrc}
				title="Admin"
				style={{
					width: '100%',
					height: '100%',
					border: 'none',
					display: 'block',
				}}
			/>
		</Box>
	);
};
